import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const formatApiError = (error) => {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!isLogin && password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      if (isLogin) {
        await login(username, password);
      } else {
        await register(username, password);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh bg-surface-0 flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8 pt-safe pb-safe">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[400px]"
        >
          {/* Logo */}
          <div className="flex items-center gap-3 mb-9">
            <div className="w-11 h-11 bg-brand-gradient rounded-2xl flex items-center justify-center shadow-elev-md">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white font-heading tracking-tight">Whisper</h1>
          </div>

          <h2 className="text-[28px] leading-tight font-bold text-white mb-2 font-heading tracking-tight">
            {isLogin ? 'Welcome back' : <>Create your <span className="text-brand-gradient">Whisper</span></>}
          </h2>
          <p className="text-zinc-400 mb-8 text-[15px]">
            {isLogin ? 'Enter your credentials to access your chats' : 'Join Whisper to start private conversations'}
          </p>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/10 border border-red-500/25 text-red-300 px-4 py-3 rounded-xl mb-6 text-sm flex items-center gap-2"
              data-testid="auth-error"
            >
              <Lock className="w-4 h-4 shrink-0" /> {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Username</label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  autoComplete="username"
                  className="ui-input h-12 pl-11 pr-4"
                  data-testid="username-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  className="ui-input h-12 pl-11 pr-11"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="ui-icon-btn ui-icon-btn-sm absolute right-1.5 top-1/2 -translate-y-1/2"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <label className="block text-sm font-medium text-zinc-300 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    className="ui-input h-12 pl-11 pr-4"
                    data-testid="confirm-password-input"
                  />
                </div>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="ui-btn ui-btn-primary w-full h-12 text-[15px] mt-1"
              data-testid="auth-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {isLogin ? 'Signing in…' : 'Creating account…'}
                </>
              ) : (
                isLogin ? 'Sign in' : 'Create account'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-zinc-400 text-sm">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="text-cyan-300 hover:text-cyan-200 font-semibold transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </motion.div>
      </div>

      {/* Right side - Branded panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-surface-1 border-l border-white/[0.05]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_28%_18%,hsl(217_91%_55%_/_0.22),transparent_52%),radial-gradient(circle_at_75%_80%,hsl(168_84%_42%_/_0.16),transparent_50%)]" />
        <div className="absolute inset-0 opacity-[0.35] [background-image:linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] [background-size:44px_44px]" />
        <div className="relative z-10 flex items-center justify-center w-full">
          <div className="text-center px-14 max-w-lg">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="w-20 h-20 bg-brand-gradient rounded-[28px] flex items-center justify-center mx-auto mb-7 shadow-elev-lg">
                <MessageCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-[32px] leading-tight font-bold text-white mb-4 font-heading tracking-tight">Private Conversations</h3>
              <p className="text-zinc-400 text-[17px] leading-relaxed">
                Create or join secret channels with a 6-letter code. No emails, no phone numbers — just pure privacy.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
